import { CSSProperties, ElementType, forwardRef, HTMLAttributes } from 'react'
import helveticaNeueMetrics from '@capsizecss/metrics/helveticaNeue'
import robotoCondensedMetrics from '@capsizecss/metrics/robotoCondensed'
import { Slot } from '@radix-ui/react-slot'
import { cva, VariantProps } from 'class-variance-authority'
import parse from 'html-react-parser'

import { removeParagraph } from '@/utils/strings'

import { cn } from '@/lib/utils'
import {
  createTrimScale,
  type FontMetrics,
  getTrimStyles,
  trimClassName
} from '@/typography-trim'

const typographyVariants = cva('', {
  variants: {
    variant: {
      huge: 'text-huge font-helvetica wrap-anywhere',
      big: 'text-big font-helvetica wrap-anywhere',
      h1: 'text-h1 font-helvetica wrap-anywhere',
      h2: 'text-h2 font-helvetica wrap-anywhere',
      h3: 'text-h3 font-helvetica wrap-anywhere',
      h4: 'text-h4 font-helvetica wrap-anywhere',
      h5: 'text-h5 font-helvetica wrap-anywhere',
      h6: 'text-h6 font-helvetica wrap-anywhere',
      large: 'text-large font-helvetica font-light',
      regular: 'text-regular font-helvetica font-light',
      small: 'text-small font-helvetica font-light',
      caption: 'text-caption font-helvetica font-normal',
      overline: 'text-overline font-helvetica font-normal uppercase',
      menu: 'text-menu font-helvetica font-medium',
      menuSecondary: 'text-menu-secondary font-helvetica font-medium'
    }
  },
  defaultVariants: {
    variant: 'regular'
  }
})

type TypographyVariant = NonNullable<
  VariantProps<typeof typographyVariants>['variant']
>

const trimSpecs: Record<
  TypographyVariant,
  {
    mobile: { fontSize: number; lineHeight: number }
    desktop?: { fontSize: number; lineHeight: number }
  }
> = {
  huge: {
    mobile: { fontSize: 120, lineHeight: 108 },
    desktop: { fontSize: 240, lineHeight: 216 }
  },
  big: {
    mobile: { fontSize: 60, lineHeight: 54 },
    desktop: { fontSize: 120, lineHeight: 108 }
  },
  h1: {
    mobile: { fontSize: 61.04, lineHeight: 85.456 },
    desktop: { fontSize: 76.29, lineHeight: 91.548 }
  },
  h2: {
    mobile: { fontSize: 48.83, lineHeight: 58.596 },
    desktop: { fontSize: 61.04, lineHeight: 67.144 }
  },
  h3: {
    mobile: { fontSize: 39.06, lineHeight: 46.872 },
    desktop: { fontSize: 48.83, lineHeight: 53.713 }
  },
  h4: {
    mobile: { fontSize: 31.25, lineHeight: 37.5 },
    desktop: { fontSize: 39.06, lineHeight: 46.872 }
  },
  h5: {
    mobile: { fontSize: 25, lineHeight: 30 },
    desktop: { fontSize: 31.25, lineHeight: 37.5 }
  },
  h6: {
    mobile: { fontSize: 20, lineHeight: 26 },
    desktop: { fontSize: 25, lineHeight: 27.5 }
  },
  large: {
    mobile: { fontSize: 20, lineHeight: 28 },
    desktop: { fontSize: 20, lineHeight: 28 }
  },
  regular: {
    mobile: { fontSize: 16, lineHeight: 24 },
    desktop: { fontSize: 16, lineHeight: 24 }
  },
  small: {
    mobile: { fontSize: 12.8, lineHeight: 17.92 },
    desktop: { fontSize: 12.8, lineHeight: 17.92 }
  },
  caption: {
    mobile: { fontSize: 10.24, lineHeight: 17.408 },
    desktop: { fontSize: 10.24, lineHeight: 17.408 }
  },
  overline: {
    mobile: { fontSize: 8.19, lineHeight: 13.104 },
    desktop: { fontSize: 8.19, lineHeight: 13.104 }
  },
  menu: {
    mobile: { fontSize: 16, lineHeight: 19.2 },
    desktop: { fontSize: 16, lineHeight: 19.2 }
  },
  menuSecondary: {
    mobile: { fontSize: 12.8, lineHeight: 15.36 },
    desktop: { fontSize: 12.8, lineHeight: 15.36 }
  }
}

const textTokenToVariant: Record<string, TypographyVariant> = {
  huge: 'huge',
  big: 'big',
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  large: 'large',
  regular: 'regular',
  small: 'small',
  caption: 'caption',
  overline: 'overline',
  menu: 'menu',
  'menu-secondary': 'menuSecondary'
}

const helveticaMetrics = helveticaNeueMetrics as FontMetrics
const robotoMetrics = robotoCondensedMetrics as FontMetrics

const getVariantFromToken = (token: string) => textTokenToVariant[token] ?? null

const getResponsiveTrimVariants = (
  variant: TypographyVariant,
  className?: string
) => {
  if (!className) {
    return { mobileVariant: variant, desktopVariant: variant }
  }

  let mobileVariant: TypographyVariant | null = null
  let desktopVariant: TypographyVariant | null = null

  for (const token of className.split(/\s+/).filter(Boolean)) {
    if (token.startsWith('md:text-')) {
      const nextVariant = getVariantFromToken(token.replace('md:text-', ''))

      if (nextVariant) {
        desktopVariant = nextVariant
      }
    } else if (token.startsWith('text-')) {
      const nextVariant = getVariantFromToken(token.replace('text-', ''))

      if (nextVariant) {
        mobileVariant = nextVariant
      }
    }
  }

  return {
    mobileVariant: mobileVariant ?? variant,
    desktopVariant: desktopVariant ?? mobileVariant ?? variant
  }
}

const getTrimStylesForVariant = ({
  mobileVariant,
  desktopVariant,
  isCondensed
}: {
  mobileVariant: TypographyVariant
  desktopVariant: TypographyVariant
  isCondensed: boolean
}) => {
  const metrics = isCondensed ? robotoMetrics : helveticaMetrics

  const mobileSpec = trimSpecs[mobileVariant].mobile
  const desktopSpec =
    trimSpecs[desktopVariant].desktop ?? trimSpecs[desktopVariant].mobile

  return getTrimStyles(
    createTrimScale(metrics, mobileSpec.fontSize, mobileSpec.lineHeight, {
      fontSize: desktopSpec.fontSize,
      lineHeight: desktopSpec.lineHeight
    })
  )
}

const getCondensedClassNames = (variant: TypographyVariant) => {
  if (variant === 'overline') {
    return 'condensed font-bold'
  }

  if (
    variant === 'large' ||
    variant === 'regular' ||
    variant === 'small' ||
    variant === 'caption'
  ) {
    return 'condensed font-normal'
  }

  return 'condensed'
}

export type TypographyProps = HTMLAttributes<HTMLElement> &
  VariantProps<typeof typographyVariants> &
  Partial<{
    component: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span'
    paragraph: boolean
    gradient: boolean
    asChild: boolean
    preserveLineBreaks: boolean
  }>

const Typography = forwardRef<HTMLElement, TypographyProps>(
  (
    {
      variant,
      className,
      children,
      component = false,
      paragraph = false,
      asChild = false,
      preserveLineBreaks = false,
      style,
      ...props
    },
    ref
  ) => {
    const Component = asChild
      ? Slot
      : ((component || (paragraph ? 'p' : 'span')) as ElementType)

    const content =
      typeof children === 'string'
        ? parse(removeParagraph(children).replace(/\r\n/g, '<br />'))
        : (children ?? null)

    const resolvedVariant = variant ?? 'regular'
    const isCondensed = /(^|\s)condensed($|\s)/.test(className ?? '')
    const { mobileVariant, desktopVariant } = getResponsiveTrimVariants(
      resolvedVariant,
      className
    )

    const trimStyles = getTrimStylesForVariant({
      mobileVariant,
      desktopVariant,
      isCondensed
    })

    const mergedStyles = {
      ...trimStyles,
      ...(style ?? {})
    } as CSSProperties

    return (
      <Component
        className={cn(
          typographyVariants({ variant: resolvedVariant }),
          isCondensed &&
            getCondensedClassNames(resolvedVariant) &&
            'font-roboto',
          className
        )}
        ref={ref}
        data-typography
        style={mergedStyles}
        {...props}
      >
        <span
          className={cn(
            'block',
            trimClassName,
            preserveLineBreaks && 'whitespace-pre-line'
          )}
          data-typography-child
        >
          {content}
        </span>
      </Component>
    )
  }
)

export default Typography
